import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ValuationWithClassifierRes } from '@dealstack/shared/lib/generated';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { useMemo } from 'react';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types';
import {
	NEW_VALUATION_ID,
	VALUATIONS_ROUTES,
} from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';

type UseValuationBreadcrumbsArgs =
	| { view: 'home'; dealId?: string }
	| {
			view: 'edit';
			dealId: string;
			valuation: ValuationWithClassifierRes | undefined;
	  }
	| {
			view: 'summary';
			dealId: string;
			valuation: ValuationWithClassifierRes | undefined;
	  }
	| {
			view: 'sources-and-uses';
			dealId: string;
			valuation: ValuationWithClassifierRes | undefined;
			personId?: string;
			mipId?: string;
			selectedEntityName?: string;
	  }
	| {
			view: 'breakpoint-summary';
			dealId: string;
			valuation: ValuationWithClassifierRes | undefined;
			personId?: string;
			mipId?: string;
			selectedEntityName?: string;
	  };

export const generateValuationBreadcrumbs = (
	props: UseValuationBreadcrumbsArgs,
	tValuations: ReturnType<typeof useTranslationValuations>,
	showEntityNameInSourcesAndUses = true
): AddBreadcrumbItem[] => {
	const baseBreadcrumbs = [
		{
			name: tValuations('homepage.title'),
			url: VALUATIONS_ROUTES.root({ dealId: props.dealId ?? '' }),
			dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_HOME,
		},
	];

	if (!props.dealId) {
		return baseBreadcrumbs;
	}

	switch (props.view) {
		case 'home':
			return baseBreadcrumbs;
		case 'edit':
			return [
				...baseBreadcrumbs,
				{
					name: props.valuation?.name ?? tValuations('edit.fallback_title'),
					url: VALUATIONS_ROUTES.specific({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? NEW_VALUATION_ID,
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_EDIT,
				},
			];
		case 'summary':
			return [
				...baseBreadcrumbs,
				{
					name: props.valuation?.name ?? tValuations('edit.fallback_title'),
					url: VALUATIONS_ROUTES.specific({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_EDIT,
				},
				{
					name: tValuations('summary.title'),
					url: VALUATIONS_ROUTES.summary({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_SUMMARY,
				},
			];
		case 'sources-and-uses':
			return [
				...baseBreadcrumbs,
				{
					name: props.valuation?.name ?? tValuations('edit.fallback_title'),
					url: VALUATIONS_ROUTES.specific({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_EDIT,
				},
				{
					name: tValuations('summary.title'),
					url: VALUATIONS_ROUTES.summary({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_SUMMARY,
				},
				{
					name:
						showEntityNameInSourcesAndUses && props.selectedEntityName
							? `${tValuations('sources_and_uses.title')} - ${props.selectedEntityName}`
							: tValuations('sources_and_uses.title'),
					url: VALUATIONS_ROUTES.sourcesAndUses({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
						personId: props.personId,
						mipId: props.mipId,
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_SOURCES_AND_USES,
				},
			];
		case 'breakpoint-summary':
			return [
				...baseBreadcrumbs,
				{
					name: props.valuation?.name ?? tValuations('edit.fallback_title'),
					url: VALUATIONS_ROUTES.specific({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_EDIT,
				},
				{
					name: tValuations('summary.title'),
					url: VALUATIONS_ROUTES.summary({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_SUMMARY,
				},
				{
					name:
						showEntityNameInSourcesAndUses && props.selectedEntityName
							? `${tValuations('sources_and_uses.title')} - ${props.selectedEntityName}`
							: tValuations('sources_and_uses.title'),
					url: VALUATIONS_ROUTES.sourcesAndUses({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
						personId: props.personId,
						mipId: props.mipId,
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_SOURCES_AND_USES,
				},
				{
					name: tValuations('breakpoint_summary.title'),
					url: VALUATIONS_ROUTES.breakpointSummary({
						dealId: props.dealId,
						valuationId: props.valuation?.id ?? '',
						personId: props.personId,
						mipId: props.mipId,
					}),
					dataTestId: VALUATION_ELEMENT_ID.BREADCRUMB_VALUATION_BREAKPOINT_SUMMARY,
				},
			];
		default:
			return baseBreadcrumbs;
	}
};

export function useValuationBreadcrumbs(
	props: UseValuationBreadcrumbsArgs,
	showEntityNameInSourcesAndUses = true
) {
	const tValuations = useTranslationValuations();

	const breadcrumbs = useMemo(() => {
		return generateValuationBreadcrumbs(props, tValuations, showEntityNameInSourcesAndUses);
	}, [props, tValuations, showEntityNameInSourcesAndUses]);

	useBreadcrumbs(breadcrumbs);
}
